import React, { useEffect, useState, useContext } from "react";
import { Button, Box } from "@mui/material";
import { motion } from "framer-motion";
import {
  MOTION_VAR_INITIAL,
  MOTION_VAR_ANIMATE,
  LIST_ANIMATE_VARIANTS,
} from "@constants/Animation.constants.js";
import { AuthContext } from "@contexts/AuthContext";
import { fetchRoleAccessProject, fetchProjectList } from "@libs/apiRequest.js";
import Spinner from "@common-components/Spinner/Spinner";
import ErrorMessage from "@common-components/ErrorMessage/ErrorMessage";

function ProjectList({ onSelect }) {
  const { currentUserRole } = useContext(AuthContext);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [projectList, setProjectList] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const granted_access_project =
          await fetchRoleAccessProject(currentUserRole);
        const granted_access_projectID = granted_access_project?.projectId;

        let projectListData = [];

        if (
          granted_access_projectID === "*" ||
          (Array.isArray(granted_access_projectID) &&
            granted_access_projectID.includes("*"))
        ) {
          // Access all projects
          projectListData = await fetchProjectList();
        } else if (Array.isArray(granted_access_projectID)) {
          // remove empty or falsy IDs
          const validIDs = granted_access_projectID.filter((id) => id?.trim());

          if (validIDs.length === 0) {
            // no valid project IDs → return empty array
            projectListData = [];
          } else {
            const projectPromises = validIDs.map((id) => fetchProjectList(id));
            const results = await Promise.all(projectPromises);
            projectListData = results.flat();
          }
        } else {
          // Single project ID
          projectListData = await fetchProjectList(granted_access_projectID);
        }

        setProjectList(projectListData);
      } catch (err) {
        setError(err.message || "Failed to fetch projects");
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUserRole) {
      fetchProjects();
    }
  }, [currentUserRole]);

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <motion.div
          initial={MOTION_VAR_INITIAL}
          animate={MOTION_VAR_ANIMATE}
          variants={LIST_ANIMATE_VARIANTS.variants}
          transition={LIST_ANIMATE_VARIANTS.transition}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            {projectList
              ?.filter((project) =>
                currentUserRole === "ADMIN" ||
                currentUserRole?.startsWith("ADMIN_")
                  ? true
                  : project.projectProductionReady === true,
              )
              .sort((a, b) =>
                (a.name ?? "").localeCompare(b.name ?? "", undefined, {
                  sensitivity: "base",
                }),
              )
              .map((project, index) => (
                <Button
                  key={`${project.id}-${index}`}
                  variant="contained"
                  sx={{
                    width: "200px",
                    height: "50px",
                    borderRadius: "10px",
                    padding: "10px",
                    textTransform: "capitalize",
                    fontWeight: "bold",
                    backgroundColor: "primary.main",
                    color: "white",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "white",
                      color: "primary.main",
                      boxShadow:
                        "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                    },
                  }}
                  onClick={() => onSelect(project)}
                >
                  {project.name}
                </Button>
              ))}
          </Box>
        </motion.div>
      )}
    </>
  );
}

export default ProjectList;
